<script lang="ts" setup>
    import '../../../css/choice.css'; 
    import {ActionButtonStyle,ActionButtonClass} from '../../lib/Types/Types';   
    import {ButtonDisplaySize} from '../../Enums/UiEnums';
    import {watch,reactive,onMounted} from 'vue';   
    import ActionButtonV2 from '../../components/ActionButtonV2.vue';
    import {ICsvRecord} from '../../lib/Models/CSVFileModel';  
    
    const file_success_message = "File Staged Successfully";

    const max_file_length = 100;
    const emit = defineEmits<(event: 'fileChanged', file: ICsvRecord) => void>();
    const formData = reactive <
    {    
         File: ICsvRecord
         SelectionDisabled?: boolean
    }>({   
        File: {Name: "", Size: 0, RecordCount: 0, ValidationMessage: "", Success: false, Data:[]},
        SelectionDisabled: false
    });
    const csv_button_class = `btn ${(formData.SelectionDisabled ? "btn-secondary" : "btn-primary")} align-middle label-isalus-small`; 
    const props = withDefaults(defineProps<{  
        fileImportID: number,
        maxFileSizeMB?: number,
        fileColumnHeaders: string [],
        fileExampleData?: string [],
        fileColumnCleanUp?: string []
    }>(),
    {
        maxFileSizeMB: 5,
    });
    onMounted(async () => {  
        resetFileValidation(); 
    });
    watch(() => props.fileImportID, (newValue, oldValue) => { 
        if (newValue !== oldValue) { 
            resetFileValidation(); 
        }
    });
    
    const maxFileBytes = props.maxFileSizeMB * 1024 * 1024;

    function resetFileValidation(new_validationMessage?: string) : void { 
        let validationMessage = new_validationMessage ?? "";
        if (validationMessage == "") {  
            if (!props.fileColumnHeaders || props.fileColumnHeaders.length === 0) {
                validationMessage = "Missing file column headers definition."; 
                formData.SelectionDisabled = true; 
            }   
            else {
                validationMessage = ""; 
                formData.SelectionDisabled = false; 
            }
        }   
        formData.File = {Name: "", Size: 0, RecordCount: 0, ValidationMessage: validationMessage, Success: false, Data: []};
    }        
    function onFileChange(event : Event) : void { 
        resetFileValidation(); 
        if (!formData.File) {
            return;
        }
        if (!event || !event.target) {
            formData.File.ValidationMessage = "No file selected.";
            returnFileInformation();
            return; 
        }
        const input = event.target as HTMLInputElement;
        const file = input.files?.[0];
        if (!file) {
            formData.File.ValidationMessage = "No file selected."; 
            returnFileInformation();
            return;
        }
        formData.File.Name = file.name; 
        formData.File.Size = file.size;
        
        if (file.type !== "text/csv") {
            formData.File.ValidationMessage = "Selected file is not a CSV file."; 
            returnFileInformation();
            return;
        }
        if (file.size == 0) { 
            formData.File.ValidationMessage = `File is empty.`; 
            returnFileInformation();
            return;
        }
        if (file.size > maxFileBytes) { 
            formData.File.ValidationMessage = `File size exceeds the limit of ${props.maxFileSizeMB} MB.`; 
            returnFileInformation();
            return;
        }
        if (file.name.length > max_file_length) {
            formData.File.ValidationMessage = `File name is too long. Maximum length is ${max_file_length} characters.`; 
            returnFileInformation();
            return;
        }
        const reader = new FileReader();
        reader.onload = ({ target }) => {
            if (target?.result) {
                const contents = target.result as string;
                processFileContents(contents);
                returnFileInformation();
            } else {
                resetFileValidation("Failed to read file contents.");
                returnFileInformation();
            }
        };
        reader.readAsText(file);
        (event.target as HTMLInputElement).value = ""; // Clear the file input after processing
    } 
    function returnFileInformation() : void {
        if (formData.File) {
            emit("fileChanged", formData.File);
        }
    }
    function processRecordContents(inputLine: string) : string[] { 
        const result = [];
        let current = '';
        let inQuotes = false;
    
        for (const char of inputLine) {
            if (char === '"') {
                inQuotes = !inQuotes; // toggle quote status
            } else if (char === ',' && !inQuotes) {
                result.push(current);
                current = '';
            } else {
                current += char;
            }
        }
        result.push(current); // push the last value
        return result;
    }
    function processFileContents(contents: string) : void {   
        const file_data = contents
            .split("\r\n")
            .map(line => processRecordContents(line))
            .filter(row => row.some(cell => cell.trim() !== ""));
  
        // Validate file using file header, file MUST contain the header
        const header = file_data[0].map((col: string) => col.trim().toLowerCase());
        const requiredColumns = props.fileColumnHeaders.map((col: string) => col.trim().toLowerCase());    
        const missingColumns = requiredColumns.filter(col => !header.includes(col));
        if (missingColumns.length > 0) { 
            formData.File.ValidationMessage = `Missing required columns: ${missingColumns.join(", ")}`; 
            return;
        }
        else if (requiredColumns.join(",") != header.join(",")) {
            if (header.length > requiredColumns.length) {
                formData.File.ValidationMessage = `File header has too many columns.`;
            } else {
                formData.File.ValidationMessage = `File header does not match the required format.`;
            }
            return;
        }
        
        // Remove the first row (header row)
        file_data.shift(); 
 
        formData.File.RecordCount = file_data.length;
        if (formData.File.RecordCount == 0) {
            formData.File.ValidationMessage = "File is empty.";
        } else {
            formData.File.Data = file_data.map((row: string[]) => {
            const rowData: { [key: string]: string } = {};
            row.forEach((cell, index) => {
                const columnName = props.fileColumnHeaders ? props.fileColumnHeaders[index] : `Column${index + 1}`;
                const field_cleanup = props.fileColumnCleanUp ? props.fileColumnCleanUp[index] : ``;
                if (field_cleanup == "{N}") {
                    rowData[columnName] = cell.trim().replace(/[^\d\-.]/g, "");
                }    
                else if (field_cleanup) {
                    rowData[columnName] = cell.trim().replace(new RegExp(`[${field_cleanup}]`, 'g'), "");
                }
                else {
                    rowData[columnName] = cell.trim();
                }
            });
            return rowData;
        });   
        }
        formData.File.Success = (formData.File.Name != "" && (formData.File.Size ?? 0) > 0 && (formData.File.RecordCount ?? 0) > 0 && formData.File.ValidationMessage === "");
        emit("fileChanged", formData.File); 
    }  
    async function createTemplateFile() : Promise<void> {
        const csvContent = 
            [
                props.fileColumnHeaders,
                (props.fileExampleData ? props.fileExampleData : [])
            ]
            .map(row => row.join(","))
            .join("\n");

        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.setAttribute("href", url);
        link.setAttribute("download", "FeeScheduleTemplate.csv");
        link.style.visibility = "hidden";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }  
</script>  
<style scoped> 
    @import url('../../../css/CsvFile.css');
</style>
<template>  
    <div class="row label-isalus-small ps-2">
        <div class="col-2">
            <label :class="csv_button_class" :disabled="formData.SelectionDisabled" title="Import a CSV file from your computer" style="width: 8rem; height: 2rem; cursor: pointer;" @click="$refs['csv_file_upload'].click()">
                <em :class="ActionButtonClass.Import "></em>
                <span class="ps-3">CSV File</span>
            </label>
            <input type="file" ref="csv_file_upload" :disabled="formData.SelectionDisabled" class="csv-file form-control" @change="onFileChange($event)" data-title="Choose a CSV File"/>
            <div class="pt-1" style="width: 100%; height: 1.5rem;">
                <ActionButtonV2 
                    text = "Template"
                    tooltip = "Download a template file"
                    class = "template-button btn btn-sm btn btn-primary"
                    :action="ActionButtonStyle.Export"
                    :size="ButtonDisplaySize.Small" 
                    :spinText="'Writing'"
                    :performAction="createTemplateFile"
                />    
            </div>     
        </div>
        <div class="col-10 grid-container-csv-data">
            <div>Name:</div>
            <div>
                {{((formData.File?.Name ?? "") == "" ? "No file chosen" : formData.File?.Name ?? "No file chosen" )}}
            </div>
            <div>Status:</div>
            <div>
                <span class="text-success" v-if="(((formData.File?.ValidationMessage ?? '') == '') && ((formData.File?.RecordCount ?? 0) > 0))">{{file_success_message}}</span>
                <span class="text-danger" v-else-if="(formData.File?.ValidationMessage ?? '') != ''">{{formData.File?.ValidationMessage}}</span> 
            </div>
            <div>File:</div>
            <div>
                <span v-if="(formData.File?.Size ?? 0) == 0"></span>
                <span v-else-if="(formData.File?.Size ?? 0) < 1024">{{formData.File?.Size ?? 0}} bytes</span>
                <span v-else-if="(formData.File?.Size ?? 0) < 1024 * 1024">{{((formData.File?.Size ?? 0) / 1024).toFixed(2)}} KB</span>
                <span v-else>{{((formData.File?.Size ?? 0) / (1024 * 1024)).toFixed(2)}} MB</span>
                <span v-if="(formData.File?.RecordCount ?? 0) > 0"> containing {{formData.File?.RecordCount ?? 0}} records</span>
            </div>
        </div>
    </div> 
</template>
